<script lang="ts">
	import { devices, devices_settings, devices_status, rooms, groups, scenes } from '../../../stores';
	import { real_id } from '../../../helpers/interface_helpers';
	import { getPresenceZones } from '../../Scenes/helpers/device_conditions/custom_cases/presence/presence-device-helpers';
	import { flowEditorState } from '../../../stores/flow_editor';
	import type { FlowNode } from '../types/flow-types';
	import { getFlowCardDefinition } from '../core/card-engine/registry';
	import type { CardFieldDefinition } from '../core/card-engine/types';
	import { resolveCardResourceOptions } from '../core/card-engine/resources';
	import {
		resolveVirtualComponentDescriptors,
		type VirtualComponentDescriptor,
	} from '../core/card-engine/virtual-components';
	import ScriptActionInspector from './Actions/ScriptActionInspector.svelte';
	import ActionInspector from './Actions/ActionInspector.svelte';
	import ConditionInspector from './Conditions/ConditionInspector.svelte';
	import TimeInspector from './Conditions/TimeInspector.svelte';
	import ScheduleInspector from './Conditions/ScheduleInspector.svelte';
	import SunriseSunsetInspector from './Conditions/SunriseSunsetInspector.svelte';
	import WeatherInspector from './Conditions/WeatherInspector.svelte';
	import EnergyInspector from './Conditions/EnergyInspector.svelte';
	import TokenSelector from './Shared/TokenSelector.svelte';

	export let node: FlowNode;

	$: definition = getFlowCardDefinition(node.type, String(node.data.paletteKey || ''));
	$: data = node.data as Record<string, any>;
	$: presenceDevices = (() => {
		const seen = new Set<string>();
		return Object.values($devices || {})
			.filter((device: any) => device?.id && device.type === 'S4SN-0U61X')
			.filter((device: any) => {
				const key = real_id(device.id);
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			})
			.map((device: any) => ({ id: device.id, name: device.name || device.id }));
	})();
	$: zones = data.device?.id
		? getPresenceZones(real_id(data.device.id), $devices_settings as Record<string, any>)
		: [];
	$: virtualComponents = resolveVirtualComponentDescriptors({
		devices: $devices,
		settings: $devices_settings,
		status: $devices_status,
	});

	function readPath(path: string): any {
		return path.split('.').reduce((value, key) => value?.[key], data);
	}

	function writePath(path: string, value: unknown) {
		const next = structuredClone(data);
		next.sceneData = undefined;
		next.originalAction = undefined;
		next.originalCondition = undefined;
		setPath(next, path, value);
		commit(next);
	}

	function setPath(target: Record<string, any>, path: string, value: unknown) {
		const keys = path.split('.');
		let cursor = target;
		keys.slice(0, -1).forEach((key) => {
			cursor[key] = cursor[key] && typeof cursor[key] === 'object' ? cursor[key] : {};
			cursor = cursor[key];
		});
		cursor[keys[keys.length - 1]] = value;
	}

	function commit(next: Record<string, any>) {
		const normalized = definition?.normalize?.(next) ?? next;
		flowEditorState.updateNode(node.id, { data: normalized as FlowNode['data'] });
	}

	function handleInspectorUpdate(event: CustomEvent<FlowNode>) {
		if (event.detail?.id) flowEditorState.updateNode(event.detail.id, event.detail);
	}

	function resourceOptions(field: CardFieldDefinition) {
		if (!field.resource) return [];
		return resolveCardResourceOptions(
			field.resource.kind,
			{
				devices: $devices,
				rooms: $rooms,
				groups: $groups,
				scenes: $scenes,
				zones,
			},
			field.resource.filter,
		).filter((option) => String(option.value) !== String(data.currentSceneId ?? ''));
	}

	function writeResource(field: CardFieldDefinition, value: string) {
		if (!field.resource) return;
		const next = structuredClone(data);
		next.sceneData = undefined;
		next.originalAction = undefined;
		next.originalCondition = undefined;
		setPath(next, field.path, value);
		const selected = resourceOptions(field).find((option) => String(option.value) === value);
		if (field.resource.labelPath) setPath(next, field.resource.labelPath, selected?.label || '');
		if (field.resource.typePath)
			setPath(next, field.resource.typePath, selected?.resourceType || '');
		commit(next);
	}

	function selectOptions(field: CardFieldDefinition) {
		if (!field.optionsFrom) return field.options || [];
		const key = String(readPath(field.optionsFrom.path) || '');
		return field.optionsFrom.cases[key] || field.optionsFrom.fallback || [];
	}

	function virtualComponentOptions(field: CardFieldDefinition): VirtualComponentDescriptor[] {
		const config = field.virtualComponent;
		if (!config) return [];
		return virtualComponents.filter(
			(component) =>
				(!config.types || config.types.includes(component.type)) &&
				(config.access === 'write' ? component.writable : component.readable),
		);
	}

	function writeVirtualComponent(field: CardFieldDefinition, ref: string) {
		const selected = virtualComponentOptions(field).find((component) => component.ref === ref);
		const next = structuredClone(data);
		next.sceneData = undefined;
		next.originalAction = undefined;
		next.originalCondition = undefined;
		next.component = selected ? structuredClone(selected) : undefined;
		if (selected) {
			const firstOption = selected.options?.[0]?.value;
			next.value = selected.defaultValue ?? firstOption ?? (selected.type === 'number' ? 0 : '');
		}
		commit(next);
	}

	function componentValueOptions(): Array<{ value: string; label: string }> {
		return Array.isArray(data.component?.options) ? data.component.options : [];
	}

	function isVisible(field: CardFieldDefinition): boolean {
		if (!field.visibleWhen) return true;
		const current = readPath(field.visibleWhen.path);
		if (field.visibleWhen.oneOf) return field.visibleWhen.oneOf.includes(current);
		return current === field.visibleWhen.equals;
	}

	function tokenValue(event: CustomEvent): string {
		return String(event.detail?.value ?? event.detail ?? '');
	}

	function selectPresenceDevice(event: CustomEvent) {
		const id = tokenValue(event);
		const selected = presenceDevices.find((device) => device.id === id);
		const next = structuredClone(data);
		next.sceneData = undefined;
		next.device = selected ? { ...selected, channel: '0' } : undefined;
		next.zoneId = '';
		next.zoneName = '';
		flowEditorState.updateNode(node.id, { data: next as FlowNode['data'] });
	}

	function selectPresenceZone(event: CustomEvent) {
		const zoneId = tokenValue(event);
		const selected = zones.find((zone) => zone.id === zoneId);
		const next = structuredClone(data);
		next.sceneData = undefined;
		next.zoneId = zoneId;
		next.zoneName = selected?.title || '';
		flowEditorState.updateNode(node.id, { data: next as FlowNode['data'] });
	}
</script>

{#if definition}
	<div class="template-inspector">
		{#each definition.fields as field}
			{#if isVisible(field)}
				{#if field.type === 'info'}
					<div class="template-info">{field.content}</div>
				{:else if field.type === 'script-configuration'}
					<ScriptActionInspector nodeId={node.id} />
				{:else if field.type === 'device-action-configuration'}
					<ActionInspector {node} on:update={handleInspectorUpdate} />
				{:else if field.type === 'device-condition-configuration'}
					<ConditionInspector {node} on:update={handleInspectorUpdate} />
				{:else if field.type === 'time-configuration'}
					<TimeInspector {node} on:update={handleInspectorUpdate} />
				{:else if field.type === 'schedule-configuration'}
					<ScheduleInspector {node} />
				{:else if field.type === 'solar-configuration'}
					<SunriseSunsetInspector nodeId={node.id} />
				{:else if field.type === 'weather-configuration'}
					<WeatherInspector nodeId={node.id} />
				{:else if field.type === 'energy-configuration'}
					<EnergyInspector nodeId={node.id} />
				{:else}
					<label>
						<span>{field.label}</span>
						{#if field.type === 'device'}
							<TokenSelector
								ariaLabel={field.label}
								options={presenceDevices.map((device) => ({
									title: device.name,
									value: device.id,
								}))}
								value={data.device?.id || ''}
								on:change={selectPresenceDevice}
							/>
						{:else if field.type === 'presence-zone'}
							<TokenSelector
								ariaLabel={field.label}
								options={zones.map((zone) => ({ title: zone.title, value: zone.id }))}
								value={String(readPath(field.path) || '')}
								disabled={!data.device?.id}
								on:change={selectPresenceZone}
							/>
						{:else if field.type === 'resource'}
							<TokenSelector
								ariaLabel={field.label}
								options={resourceOptions(field).map((option) => ({
									title: option.label,
									value: option.value,
								}))}
								value={String(readPath(field.path) ?? '')}
								on:change={(event) => writeResource(field, tokenValue(event))}
							/>
						{:else if field.type === 'virtual-component'}
							<TokenSelector
								ariaLabel={field.label}
								options={virtualComponentOptions(field).map((component) => ({
									title: `${component.parentDeviceName} / ${component.name}`,
									value: component.ref,
								}))}
								value={String(data.component?.ref || '')}
								on:change={(event) => writeVirtualComponent(field, tokenValue(event))}
							/>
						{:else if field.type === 'component-value'}
							{#if data.component?.type === 'boolean' || data.component?.type === 'enum'}
								<TokenSelector
									ariaLabel={field.label}
									options={componentValueOptions().map((option) => ({
										title: option.label,
										value: option.value,
									}))}
									value={String(readPath(field.path) ?? '')}
									on:change={(event) => writePath(field.path, tokenValue(event))}
								/>
							{:else}
								<input
									type={data.component?.type === 'number' ? 'number' : 'text'}
									value={readPath(field.path) ?? ''}
									min={data.component?.min}
									max={data.component?.max}
									step={data.component?.step}
									maxlength={data.component?.maxLength}
									disabled={!data.component?.ref}
									on:input={(event) =>
										writePath(field.path, event.currentTarget.value)}
								/>
							{/if}
						{:else if field.type === 'select'}
							<TokenSelector
								ariaLabel={field.label}
								options={selectOptions(field).map((option) => ({
									title: option.label,
									value: option.value,
								}))}
								value={String(readPath(field.path) ?? '')}
								on:change={(event) => writePath(field.path, tokenValue(event))}
							/>
						{:else if field.type === 'textarea'}
							<textarea
								value={readPath(field.path) ?? ''}
								placeholder={field.placeholder || ''}
								on:input={(event) => writePath(field.path, event.currentTarget.value)}
							></textarea>
						{:else}
							<input
								type={field.type === 'number' ? 'number' : 'text'}
								value={readPath(field.path) ?? ''}
								placeholder={field.placeholder || ''}
								min={field.min}
								max={field.max}
								step={field.step}
								on:input={(event) =>
									writePath(
										field.path,
										field.type === 'number'
											? Number(event.currentTarget.value)
											: event.currentTarget.value,
									)}
							/>
						{/if}
						{#if field.helperText}<small>{field.helperText}</small>{/if}
					</label>
				{/if}
			{/if}
		{/each}
	</div>
{/if}

<style>
	.template-inspector {
		display: flex;
		flex-direction: column;
		gap: 0.7rem;
	}
	.template-description,
	.template-summary {
		margin: 0;
		font-size: 0.78rem;
		line-height: 1.45;
		color: #9ca3af;
	}
	.template-info {
		padding: 0.7rem;
		border-radius: 8px;
		background: rgba(59, 130, 246, 0.1);
		font-size: 0.78rem;
		line-height: 1.45;
	}
	.template-summary {
		padding: 0.65rem;
		border-radius: 8px;
		background: rgba(6, 182, 212, 0.1);
		color: inherit;
	}
	label {
		display: flex;
		flex-direction: column;
		gap: 0.3rem;
		font-size: 0.75rem;
		color: #9ca3af;
	}
	input,
	textarea,
	select {
		min-height: 36px;
		border-radius: 8px;
		border: 1px solid rgba(148, 163, 184, 0.35);
		background: var(--flow-panel-field, rgba(255, 255, 255, 0.06));
		color: inherit;
		padding: 0 10px;
	}
	textarea {
		min-height: 76px;
		padding: 9px 10px;
		resize: vertical;
	}
	select:disabled {
		opacity: 0.55;
	}
</style>
